/**
 * Per-page bbox cache for the editor lazy-load redesign.
 *
 * <p>Backs the page-1-paint hot path (single-page WASM call ~20-30 ms vs
 * full-doc walk ~2-5s on a 60-page PDF) and the post-Tier1 / viewport-
 * vicinity prefetch chains. Promise-keyed so concurrent requests for the
 * same page collapse onto one WASM round-trip.</p>
 *
 *
 * <p>Invariants — each is pinned by `bbox-cache.test.mjs`:</p>
 * <ol>
 *   <li>Cache miss issues exactly one WASM round-trip per page.</li>
 *   <li>Cache hit returns the cached Promise; no WASM round-trip.</li>
 *   <li>{@link prefetchRange} populates the cache for every in-range
 *       page that has at least one bbox entry.</li>
 *   <li>{@link prefetchRange} MUST NOT overwrite an existing cache
 *       entry (JS-side dedup, §3.3 strategy b).</li>
 *   <li>{@link clear} releases all cached entries — invalidation hook
 *       for `applyZoom` and `resetEditorState`.</li>
 *   <li>{@link invalidatePages} narrow-evicts the listed pages —
 *       wired to geometry-op dispatches (set-bbox / create-element /
 *       delete-element / reorder-reading) and to the
 *       `MultiPageScrollManager.demote` viewport-exit hook so the
 *       cache no longer grows monotonically.
 *       See `docs/design/frontend-mv-architecture-260524.md` §P.7.</li>
 *   <li>A failed WASM call evicts its (rejected) Promise from the cache
 *       so a retry can succeed; without the eviction a transient
 *       hiccup permanently blanks the overlay.</li>
 * </ol>
 */
/**
 * Construct a fresh per-page bbox cache instance.
 *
 * <p>Returns a closure-bound API rather than a class so test seams are
 * straightforward: each test gets a clean instance. The production
 * editor.js holds one instance for the lifetime of the open document
 * (cleared on zoom + reset).</p>
 *
 * Lazy accessor returning the live interop, or null if not yet ready.
 * The bbox cache is constructed before interop is available, so we
 * defer resolution until first call.
 */
export function createBboxCache(interopProvider) {
    const _byPage = new Map();
    async function getForPage(pageNum) {
        const cached = _byPage.get(pageNum);
        if (cached !== undefined)
            return cached;
        const interop = interopProvider();
        if (!interop)
            return [];
        const idx0 = pageNum - 1;
        const promise = (async () => {
            try {
                const result = await interop.getBoundingBoxesRange(idx0, idx0);
                return Array.isArray(result) ? result : [];
            }
            catch (err) {
                // Eject the rejected Promise so a retry can succeed.
                _byPage.delete(pageNum);
                throw err;
            }
        })();
        _byPage.set(pageNum, promise);
        return promise;
    }
    async function prefetchRange(start, end) {
        const interop = interopProvider();
        if (!interop)
            return;
        if (start < 1)
            start = 1;
        const idx0Start = start - 1;
        const idx0End = (end === -1) ? -1 : (end - 1);
        let result;
        try {
            result = await interop.getBoundingBoxesRange(idx0Start, idx0End);
        }
        catch {
            // Best-effort: prefetch failures must not throw to the caller
            // because they're typically scheduled via requestIdleCallback
            // / setTimeout(0) — there's nothing to surface to the user
            // and an unhandled rejection would log noise. Hot-path callers
            // (getForPage) still surface their errors.
            return;
        }
        if (!Array.isArray(result))
            return;
        // Group entries by page; skip the page-0 sentinel (only emitted on
        // full-doc walks for struct elems with no MCR content — not a
        // per-page cache row).
        const byPage = new Map();
        for (const entry of result) {
            if (!entry || typeof entry.page !== "number" || entry.page < 1)
                continue;
            let arr = byPage.get(entry.page);
            if (!arr) {
                arr = [];
                byPage.set(entry.page, arr);
            }
            arr.push(entry);
        }
        for (const [pageNum, bboxes] of byPage) {
            if (_byPage.has(pageNum))
                continue; // dedup: keep existing
            _byPage.set(pageNum, Promise.resolve(bboxes));
        }
    }
    /** Drop every cached entry. Wired to applyZoom and resetEditorState. */
    function clear() { _byPage.clear(); }
    /**
     * Narrow-evict the listed pages. Wired to:
     *   - the dispatcher hook for geometry ops (set-bbox / create-element /
     *     delete-element / reorder-reading) — see `dispatcher.ts`
     *     `shouldInvalidateBboxCache`;
     *   - the `MultiPageScrollManager.demote` viewport-exit hook —
     *     see `editor-multipage.ts`.
     *
     * Page 0 (the doc-level sentinel emitted by some reducer paths) is a
     * safe no-op because no entry is ever inserted under page 0.
     *
     * See `docs/design/frontend-mv-architecture-260524.md` §P.7.
     */
    function invalidatePages(pages) {
        for (const p of pages)
            _byPage.delete(p);
    }
    /** Test/diagnostic accessors. */
    function size() { return _byPage.size; }
    function has(pageNum) { return _byPage.has(pageNum); }
    function get(pageNum) { return _byPage.get(pageNum); }
    return { getForPage, prefetchRange, clear, invalidatePages, size, has, get };
}
